import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthUser from "./AuthUser";
import toast, { Toaster } from "react-hot-toast";
import ExpenseSearchSelect, {
    mapToSelectOptions,
    mapVendorSelectOptions,
    formatVendorOptionLabel,
} from "./ExpenseSearchSelect";
import "./ExpenseModule.css";
import { getTodayDate, toDateInputValue } from "../utils/financialYear";

const NAVY = "#1a237e";
const NAVY_GRADIENT = "linear-gradient(135deg, #1a237e 0%, #283593 100%)";

const emptyLineForm = {
    expense_item_master_id: "",
    quantity: "1",
    price: "",
    gst_percent: "",
    description: "",
};

const fmt = (v) => Math.round(parseFloat(v || 0)).toLocaleString("en-IN");

const amountWithGst = (quantity, price, gstPercent) => {
    const qty = parseFloat(quantity) || 0;
    const unitPrice = parseFloat(price) || 0;
    const gst = parseFloat(gstPercent) || 0;
    const gross = qty * unitPrice;
    if (gst > 0) {
        return gross * (1 + gst / 100);
    }
    return gross;
};

export default function ExpenseItemAdd() {
    const { http, user } = AuthUser();
    const navigate = useNavigate();
    const itemSelectRef = useRef(null);
    const qtyRef = useRef(null);
    const priceRef = useRef(null);
    const gstRef = useRef(null);
    const noteRef = useRef(null);

    const [vendors, setVendors] = useState([]);
    const [itemMasters, setItemMasters] = useState([]);
    const [draft, setDraft] = useState(null);
    const [vendorId, setVendorId] = useState("");
    const [voucherDate, setVoucherDate] = useState(getTodayDate());
    const [voucherDateKey, setVoucherDateKey] = useState(0);
    const [description, setDescription] = useState("");
    const [lineForm, setLineForm] = useState(emptyLineForm);
    const [editLineId, setEditLineId] = useState(null);
    const [loadingDraft, setLoadingDraft] = useState(false);
    const [addingLine, setAddingLine] = useState(false);
    const [generating, setGenerating] = useState(false);

    useEffect(() => {
        http.get(`/expense_vendor_masters?login_user=${user.id}`)
            .then((res) => { if (res.data.success) setVendors(res.data.data || []); })
            .catch(() => toast.error("Error loading vendors"));

        loadItemMasters();
        loadDraft();
    }, []);

    useEffect(() => {
        if (draft && itemSelectRef.current) {
            itemSelectRef.current.focus();
        }
    }, [draft?.id]);

    const loadItemMasters = () => {
        http.get(`/expense_item_masters?login_user=${user.id}`)
            .then((res) => { if (res.data.success) setItemMasters(res.data.data || []); })
            .catch(() => toast.error("Error loading items"));
    };

    const loadDraft = async () => {
        setLoadingDraft(true);
        try {
            const res = await http.get(`/expense_voucher_draft?login_user=${user.id}`);
            if (res.data.success && res.data.data) {
                setDraft(res.data.data);
                setVendorId(String(res.data.data.expense_vendor_master_id));
                setVoucherDate(
                    res.data.data.voucher_date
                        ? toDateInputValue(res.data.data.voucher_date)
                        : getTodayDate()
                );
                setVoucherDateKey((k) => k + 1);
                setDescription(res.data.data.description || "");
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingDraft(false);
        }
    };

    const startDraft = async (selectedVendorId, note = description) => {
        if (!selectedVendorId) return;
        setLoadingDraft(true);
        try {
            const res = await http.post(`/expense_voucher_draft?login_user=${user.id}`, {
                expense_vendor_master_id: parseInt(selectedVendorId, 10),
                voucher_date: voucherDate || getTodayDate(),
                description: note.trim() || null,
            });
            if (res.data.success) {
                setDraft(res.data.data);
                const savedDate = toDateInputValue(res.data.data?.voucher_date);
                if (savedDate) {
                    setVoucherDate(savedDate);
                    setVoucherDateKey((k) => k + 1);
                }
                setLineForm(emptyLineForm);
                setEditLineId(null);
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Error starting voucher");
        } finally {
            setLoadingDraft(false);
        }
    };

    const handleVendorChange = async (e) => {
        const value = e.target.value;
        setVendorId(value);
        if (value) {
            await startDraft(value, description);
        } else {
            setDraft(null);
            setEditLineId(null);
            setLineForm(emptyLineForm);
        }
    };

    const persistDraftMeta = async (nextDate = voucherDate, nextDescription = description) => {
        if (!draft?.id) return;
        const dateValue = toDateInputValue(nextDate) || getTodayDate();
        try {
            const res = await http.put(`/expense_voucher_draft/${draft.id}?login_user=${user.id}`, {
                voucher_date: dateValue,
                description: (nextDescription || "").trim() || null,
            });
            if (res.data.success) {
                setDraft(res.data.data);
                const savedDate = toDateInputValue(res.data.data?.voucher_date) || dateValue;
                setVoucherDate(savedDate);
                if (savedDate !== dateValue) {
                    setVoucherDateKey((k) => k + 1);
                }
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Error updating voucher details");
        }
    };

    // Uncontrolled date input: avoid React rewriting segments while typing (e.g. 15 → 10).
    const handleVoucherDateChange = (e) => {
        const value = e.target.value;
        if (value) setVoucherDate(value);
    };

    const handleVoucherDateBlur = async (e) => {
        const value = toDateInputValue(e.target.value) || getTodayDate();
        if (value !== voucherDate) setVoucherDate(value);
        if (!e.target.value) {
            e.target.value = value;
        }
        await persistDraftMeta(value, description);
    };

    const handleDescriptionBlur = async () => {
        await persistDraftMeta(voucherDate, description);
    };

    const handleLineFormChange = (e) => {
        const { name, value } = e.target;
        setLineForm((prev) => ({ ...prev, [name]: value }));
    };

    const resetLineForm = () => {
        setEditLineId(null);
        setLineForm(emptyLineForm);
        setTimeout(() => itemSelectRef.current?.focus(), 50);
    };

    const startEditLine = (row) => {
        setEditLineId(row.id);
        setLineForm({
            expense_item_master_id: row.expense_item_master_id ? String(row.expense_item_master_id) : "",
            quantity: row.quantity !== null && row.quantity !== undefined ? String(row.quantity) : "1",
            price: row.price !== null && row.price !== undefined ? String(row.price) : "",
            gst_percent: row.gst_percent !== null && row.gst_percent !== undefined ? String(row.gst_percent) : "",
            description: row.description || "",
        });
        setTimeout(() => qtyRef.current?.focus(), 50);
    };

    const lineAmount = amountWithGst(lineForm.quantity, lineForm.price, lineForm.gst_percent);
    const lines = draft?.lines || [];
    const lineTotal = parseFloat(draft?.total_amount || 0);

    const applyItemMasterDefaults = (itemMasterId) => {
        if (!itemMasterId) {
            setLineForm((prev) => ({
                ...prev,
                expense_item_master_id: "",
                price: "",
                gst_percent: "",
            }));
            return;
        }
        const master = itemMasters.find((m) => String(m.id) === String(itemMasterId));
        setLineForm((prev) => ({
            ...prev,
            expense_item_master_id: itemMasterId,
            price: master && master.price !== null && master.price !== undefined ? String(master.price) : "",
            gst_percent: master && master.gst_percent !== null && master.gst_percent !== undefined ? String(master.gst_percent) : "",
        }));
    };

    const saveLineToVoucher = async (e) => {
        if (e) e.preventDefault();
        if (!draft) {
            toast.error("Please select vendor first");
            return;
        }
        if (!lineForm.expense_item_master_id) {
            toast.error("Please select an item");
            itemSelectRef.current?.focus();
            return;
        }
        if (!lineForm.quantity || parseFloat(lineForm.quantity) <= 0) {
            toast.error("Please enter valid quantity");
            qtyRef.current?.focus();
            return;
        }
        if (lineForm.price === "" || parseFloat(lineForm.price) < 0) {
            toast.error("Please enter valid price");
            priceRef.current?.focus();
            return;
        }
        if (lineForm.gst_percent !== "" && (parseFloat(lineForm.gst_percent) < 0 || parseFloat(lineForm.gst_percent) > 100)) {
            toast.error("Please enter valid GST %");
            gstRef.current?.focus();
            return;
        }

        const payload = {
            expense_item_master_id: parseInt(lineForm.expense_item_master_id, 10),
            quantity: parseFloat(lineForm.quantity),
            price: parseFloat(lineForm.price),
            gst_percent: lineForm.gst_percent === "" ? 0 : parseFloat(lineForm.gst_percent),
            description: lineForm.description.trim() || null,
        };

        setAddingLine(true);
        try {
            const res = editLineId
                ? await http.put(`/expense_voucher_line/${editLineId}?login_user=${user.id}`, payload)
                : await http.post(`/expense_voucher/${draft.id}/line?login_user=${user.id}`, payload);

            if (res.data.success) {
                setDraft(res.data.data);
                resetLineForm();
                toast.success(editLineId ? "Item updated!" : "Item added!");
            }
        } catch (err) {
            toast.error(err.response?.data?.message || (editLineId ? "Error updating item" : "Error adding item"));
        } finally {
            setAddingLine(false);
        }
    };

    const handleEntryKeyDown = (e, field) => {
        if (e.key === "Enter") {
            e.preventDefault();
            if (field === "item" && lineForm.expense_item_master_id) {
                qtyRef.current?.focus();
            } else if (field === "qty") {
                priceRef.current?.focus();
            } else if (field === "price") {
                gstRef.current?.focus();
            } else if (field === "gst") {
                noteRef.current?.focus();
            } else if (field === "note") {
                saveLineToVoucher();
            }
        }
    };

    const removeLine = async (lineId) => {
        try {
            const res = await http.delete(`/expense_voucher_line/${lineId}`);
            if (res.data.success) {
                setDraft(res.data.data);
                if (editLineId === lineId) {
                    resetLineForm();
                }
            }
        } catch (err) {
            toast.error("Error removing item");
        }
    };

    const handleGenerateVoucher = async () => {
        if (!draft) {
            toast.error("Please select vendor first");
            return;
        }
        if (lines.length === 0) {
            toast.error("Add at least one item to the voucher");
            return;
        }

        setGenerating(true);
        try {
            const res = await http.post(`/expense_voucher/${draft.id}/generate?login_user=${user.id}`);
            if (res.data.success) {
                toast.success(res.data.message || "Voucher generated!");
                navigate("/expense_item_list");
            } else {
                toast.error(res.data.message || "Error generating voucher");
            }
        } catch (err) {
            toast.error(err.response?.data?.message || "Error generating voucher");
        } finally {
            setGenerating(false);
        }
    };

    const selectedVendor = vendors.find((v) => String(v.id) === vendorId);
    const selectedMaster = itemMasters.find((m) => String(m.id) === lineForm.expense_item_master_id);

    const vendorOptions = mapVendorSelectOptions(vendors);
    const itemOptions = mapToSelectOptions(itemMasters, {
        labelFn: (m) => `${m.head ? `${m.head.name} — ` : ""}${m.name}${m.code ? ` (${m.code})` : ""}`,
    });

    const vendorSelectStyles = {
        option: (base, state) => ({
            ...base,
            fontSize: "0.875rem",
            paddingTop: 8,
            paddingBottom: 8,
            backgroundColor: state.isSelected ? NAVY : state.isFocused ? "#e8eaf6" : "#fff",
            color: state.isSelected ? "#fff" : "#333",
        }),
    };

    return (
        <>
            <Toaster />
            <div className="container-fluid mt-3 px-3 pb-4 expense-module">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                        <h5 className="fw-bold mb-0">Voucher Create</h5>
                        <small className="text-muted">Select vendor, then add items with Tab ↹ and Enter</small>
                    </div>
                    <div className="d-flex gap-2">
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => {
                                loadItemMasters();
                                loadDraft();
                                http.get(`/expense_vendor_masters?login_user=${user.id}`)
                                    .then((res) => { if (res.data.success) setVendors(res.data.data || []); })
                                    .catch(() => toast.error("Error loading vendors"));
                            }}
                            disabled={loadingDraft}
                            title="Refresh"
                        >
                            <i className={`fas fa-sync-alt me-1 ${loadingDraft ? "fa-spin" : ""}`}></i>Refresh
                        </button>
                        <Link to="/expense_item_list" className="btn btn-sm btn-outline-primary">
                            <i className="fas fa-list me-1"></i>Voucher List
                        </Link>
                    </div>
                </div>

                {vendors.length === 0 && (
                    <div className="alert alert-warning py-2 small">
                        No vendors found. <Link to="/expense_vendor_master_list">Add vendors</Link> first.
                    </div>
                )}

                <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body">
                        <div className="row g-3">
                            <div className="col-md-5">
                                <label className="form-label small fw-semibold mb-1">Voucher Vendor *</label>
                                <ExpenseSearchSelect
                                    options={vendorOptions}
                                    value={vendorId}
                                    onChange={(val) => handleVendorChange({ target: { value: val } })}
                                    placeholder="Type to search vendor or address..."
                                    isDisabled={loadingDraft || lines.length > 0}
                                    isClearable={lines.length === 0 && !loadingDraft}
                                    formatOptionLabel={formatVendorOptionLabel}
                                    styles={vendorSelectStyles}
                                />
                                {lines.length > 0 && (
                                    <small className="text-muted">Remove all items to change vendor</small>
                                )}
                            </div>
                            <div className="col-md-2">
                                <label className="form-label small fw-semibold mb-1">Voucher Date</label>
                                <input
                                    key={`voucher-date-${voucherDateKey}`}
                                    type="date"
                                    className="form-control form-control-sm"
                                    defaultValue={voucherDate}
                                    onChange={handleVoucherDateChange}
                                    onBlur={handleVoucherDateBlur}
                                />
                            </div>
                            <div className="col-md-5">
                                <label className="form-label small fw-semibold mb-1">Voucher Note (optional)</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    onBlur={handleDescriptionBlur}
                                    placeholder="Short note for this voucher"
                                    disabled={!draft}
                                />
                            </div>
                        </div>

                        {selectedVendor && (
                            <div className="mt-3 p-2 rounded small d-flex flex-wrap gap-3" style={{ background: "#e8eaf6" }}>
                                <span><i className="fas fa-store me-1" style={{ color: NAVY }}></i><strong>{selectedVendor.name}</strong></span>
                                {selectedVendor.address && (
                                    <span className="text-muted">
                                        <i className="fas fa-map-marker-alt me-1"></i>
                                        {selectedVendor.address}
                                    </span>
                                )}
                                {selectedVendor.phone && <span className="text-muted"><i className="fas fa-phone me-1"></i>{selectedVendor.phone}</span>}
                                {selectedVendor.gst_no && <span className="text-muted"><i className="fas fa-receipt me-1"></i>{selectedVendor.gst_no}</span>}
                            </div>
                        )}
                    </div>
                </div>

                <div className="card border-0 shadow-sm overflow-hidden">
                    <div className="px-3 py-2 d-flex justify-content-between align-items-center text-white" style={{ background: NAVY_GRADIENT }}>
                        <span className="fw-semibold"><i className="fas fa-shopping-cart me-2"></i>Voucher Items</span>
                        <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-light text-dark">{lines.length} line(s)</span>
                            {lines.length > 0 && (
                                <span className="badge" style={{ background: "rgba(255,255,255,0.2)" }}>Total: {fmt(lineTotal)}</span>
                            )}
                        </div>
                    </div>

                    {!draft ? (
                        <div className="text-center text-muted py-5">
                            <i className="fas fa-hand-pointer fa-2x mb-3 opacity-25"></i>
                            <p className="mb-0 small">Select a vendor above to start adding items</p>
                        </div>
                    ) : (
                        <>
                            <div className="px-3 py-2 border-bottom" style={{ background: editLineId ? "#fff8e1" : "#f8f9ff" }}>
                                <div className="d-flex align-items-center justify-content-between mb-2">
                                    <span className="small fw-semibold" style={{ color: NAVY }}>
                                        <i className={`fas ${editLineId ? "fa-edit" : "fa-bolt"} me-1`}></i>
                                        {editLineId ? "Edit Item" : "Quick Add"}
                                    </span>
                                    <span className="small text-muted">
                                        {editLineId ? "Update the line below, then click Update" : "Tab ↹ next field · Enter on Note to add"}
                                    </span>
                                </div>
                                <form onSubmit={saveLineToVoucher}>
                                    <div className="row g-2 align-items-start">
                                        <div className="col-lg-3">
                                            <label className="form-label small mb-1 text-muted d-block">Item</label>
                                            <ExpenseSearchSelect
                                                selectRef={itemSelectRef}
                                                options={itemOptions}
                                                value={lineForm.expense_item_master_id}
                                                onChange={applyItemMasterDefaults}
                                                onKeyDown={(e) => handleEntryKeyDown(e, "item")}
                                                placeholder="Type to search item..."
                                                isDisabled={addingLine}
                                                isClearable
                                            />
                                            <div className="small text-muted mt-1 text-truncate" style={{ minHeight: "1rem" }}>
                                                {selectedMaster?.head ? selectedMaster.head.name : "\u00a0"}
                                            </div>
                                        </div>
                                        <div className="col-4 col-lg-1">
                                            <label className="form-label small mb-1 text-muted d-block">Qty</label>
                                            <input
                                                ref={qtyRef}
                                                type="number"
                                                step="0.01"
                                                min="0.01"
                                                name="quantity"
                                                className="form-control form-control-sm text-end"
                                                value={lineForm.quantity}
                                                onChange={handleLineFormChange}
                                                onKeyDown={(e) => handleEntryKeyDown(e, "qty")}
                                                disabled={addingLine}
                                            />
                                            <div className="mt-1" style={{ minHeight: "1rem" }} aria-hidden="true">&nbsp;</div>
                                        </div>
                                        <div className="col-4 col-lg-1">
                                            <label className="form-label small mb-1 text-muted d-block">Price</label>
                                            <input
                                                ref={priceRef}
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                name="price"
                                                className="form-control form-control-sm text-end"
                                                value={lineForm.price}
                                                onChange={handleLineFormChange}
                                                onKeyDown={(e) => handleEntryKeyDown(e, "price")}
                                                disabled={addingLine}
                                                placeholder="0.00"
                                            />
                                            <div className="mt-1" style={{ minHeight: "1rem" }} aria-hidden="true">&nbsp;</div>
                                        </div>
                                        <div className="col-4 col-lg-1">
                                            <label className="form-label small mb-1 text-muted d-block">GST %</label>
                                            <input
                                                ref={gstRef}
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max="100"
                                                name="gst_percent"
                                                className="form-control form-control-sm text-end"
                                                value={lineForm.gst_percent}
                                                onChange={handleLineFormChange}
                                                onKeyDown={(e) => handleEntryKeyDown(e, "gst")}
                                                disabled={addingLine}
                                                placeholder="0"
                                            />
                                            <div className="mt-1" style={{ minHeight: "1rem" }} aria-hidden="true">&nbsp;</div>
                                        </div>
                                        <div className="col-6 col-lg-2">
                                            <label className="form-label small mb-1 text-muted d-block">Amount (incl. GST)</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm text-end fw-semibold bg-white"
                                                value={fmt(lineAmount)}
                                                readOnly
                                                tabIndex={-1}
                                                title="Qty × Price + GST"
                                            />
                                            <div className="mt-1 small text-muted" style={{ minHeight: "1rem" }}>
                                                + GST
                                            </div>
                                        </div>
                                        <div className="col-lg-3">
                                            <label className="form-label small mb-1 text-muted d-block">Note</label>
                                            <input
                                                ref={noteRef}
                                                type="text"
                                                name="description"
                                                className="form-control form-control-sm"
                                                value={lineForm.description}
                                                onChange={handleLineFormChange}
                                                onKeyDown={(e) => handleEntryKeyDown(e, "note")}
                                                placeholder="Optional"
                                                disabled={addingLine}
                                            />
                                            <div className="mt-1" style={{ minHeight: "1rem" }} aria-hidden="true">&nbsp;</div>
                                        </div>
                                        <div className="col-lg-1">
                                            <label className="form-label small mb-1 d-block invisible">{editLineId ? "Update" : "Add"}</label>
                                            <div className="d-flex gap-1">
                                                <button
                                                    type="submit"
                                                    className={`btn btn-sm ${editLineId ? "btn-warning" : "btn-primary"} ${editLineId ? "" : "w-100"}`}
                                                    style={{ minHeight: 31, flex: 1 }}
                                                    disabled={addingLine}
                                                    title={editLineId ? "Update line" : "Add line (Enter on Note)"}
                                                >
                                                    {addingLine ? (
                                                        <span className="spinner-border spinner-border-sm"></span>
                                                    ) : (
                                                        <i className={`fas ${editLineId ? "fa-check" : "fa-plus"}`}></i>
                                                    )}
                                                </button>
                                                {editLineId && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-secondary"
                                                        style={{ minHeight: 31 }}
                                                        onClick={resetLineForm}
                                                        disabled={addingLine}
                                                        title="Cancel edit"
                                                    >
                                                        <i className="fas fa-times"></i>
                                                    </button>
                                                )}
                                            </div>
                                            <div className="mt-1" style={{ minHeight: "1rem" }} aria-hidden="true">&nbsp;</div>
                                        </div>
                                    </div>
                                </form>
                            </div>

                            <div className="table-responsive">
                                <table className="table table-sm table-hover mb-0 align-middle">
                                    <thead>
                                        <tr style={{ background: "#e8eaf6", color: NAVY }}>
                                            <th className="ps-3" style={{ width: 40 }}>#</th>
                                            <th>Category</th>
                                            <th>Item</th>
                                            <th className="text-end">Qty</th>
                                            <th className="text-end">Price</th>
                                            <th className="text-end">GST %</th>
                                            <th className="text-end">Amount</th>
                                            <th>Note</th>
                                            <th className="pe-3 text-end" style={{ width: 90 }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lines.length === 0 ? (
                                            <tr>
                                                <td colSpan={9} className="text-center text-muted py-4 small">
                                                    No items yet — use Quick Add above
                                                </td>
                                            </tr>
                                        ) : lines.map((row, idx) => (
                                            <tr key={row.id} className={editLineId === row.id ? "table-warning" : ""}>
                                                <td className="ps-3 text-muted">{idx + 1}</td>
                                                <td>
                                                    <span className="badge rounded-pill" style={{ background: "#e8eaf6", color: NAVY, fontWeight: 500 }}>
                                                        {row.head ? row.head.name : "—"}
                                                    </span>
                                                </td>
                                                <td className="fw-semibold">{row.item_name}</td>
                                                <td className="text-end">{row.quantity}</td>
                                                <td className="text-end">{fmt(row.price)}</td>
                                                <td className="text-end">{fmt(row.gst_percent)}</td>
                                                <td className="text-end fw-bold">{fmt(row.amount)}</td>
                                                <td className="text-muted small">{row.description || "—"}</td>
                                                <td className="pe-3 text-end">
                                                    <div className="d-flex gap-1 justify-content-end">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-primary px-2"
                                                            onClick={() => startEditLine(row)}
                                                            disabled={addingLine}
                                                            title="Edit"
                                                        >
                                                            <i className="fas fa-edit"></i>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-danger px-2"
                                                            onClick={() => removeLine(row.id)}
                                                            disabled={addingLine || editLineId === row.id}
                                                            title="Remove"
                                                        >
                                                            <i className="fas fa-times"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    {lines.length > 0 && (
                                        <tfoot>
                                            <tr style={{ background: "#fff8e1" }}>
                                                <td colSpan={6} className="text-end fw-bold ps-3 py-2">Voucher Total</td>
                                                <td className="text-end fw-bold py-2" style={{ color: "#2e7d32", fontSize: "1rem" }}>{fmt(lineTotal)}</td>
                                                <td colSpan={2} className="pe-3"></td>
                                            </tr>
                                        </tfoot>
                                    )}
                                </table>
                            </div>
                        </>
                    )}

                    <div className="card-footer py-3 d-flex justify-content-between align-items-center bg-white border-top">
                        <span className="small text-muted">
                            {draft ? `${lines.length} item(s) · ${fmt(lineTotal)}` : "Select vendor to continue"}
                        </span>
                        <button
                            type="button"
                            className="btn btn-success px-4"
                            onClick={handleGenerateVoucher}
                            disabled={generating || !draft || lines.length === 0}
                        >
                            {generating ? (
                                <><span className="spinner-border spinner-border-sm me-1"></span>Generating...</>
                            ) : (
                                <><i className="fas fa-file-invoice me-1"></i>Generate Voucher</>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}
